import type { ChatItem } from "../components/MessageBubble";
import type { EngineSnapshot } from "../components/progress/utils";
import type { CalculationLogEntry, CheckinView, PlanProfile } from "../screens/plan-utils";
import type { FrequentFoodView, LibraryEntry } from "../screens/library-utils";
import type { Doc, Id } from "../../convex/_generated/dataModel";

/**
 * Demo scenario datasets. Demo mode is a frontend-only showcase for creators
 * who screen-record the app, so every scenario is a complete, internally
 * consistent story: weight series matches the stated loss, daily totals match
 * the meal items, macro targets match the kcal target via 4/4/9, and the
 * journey percent matches start/current/goal.
 */

export type DemoScenarioId = "progress" | "fresh" | "comeback";

export const DEMO_SCENARIO_IDS: readonly DemoScenarioId[] = ["progress", "fresh", "comeback"];
export const DEFAULT_DEMO_SCENARIO: DemoScenarioId = "fresh";
export const DEMO_SCENARIO_STORAGE_KEY = "bitetrack-demo-scenario";

/** Plan demo dates are anchored to the same "today" the Plan screen used before. */
export const DEMO_PLAN_NOW = Date.UTC(2026, 6, 28);

const DAY_MS = 86_400_000;

type DemoFoodEntry = Doc<"foodEntries">;
type DemoExerciseEntry = Doc<"exerciseEntries">;
type DemoMemory = Doc<"foodMemories">;
export type DemoUserProfile = Doc<"users">;
export type DemoWeightPoint = { readonly date: number; readonly weightKg: number };

export interface DemoPlanData {
  readonly profile: PlanProfile;
  readonly snapshot: EngineSnapshot;
  readonly pending: CheckinView | null;
  readonly checkins: readonly CheckinView[];
  readonly calcHistory: readonly CalculationLogEntry[];
  readonly currentWeightKg: number;
}

export interface DemoLibraryData {
  readonly recent: readonly LibraryEntry[];
  readonly frequent: readonly FrequentFoodView[];
  readonly yesterday: readonly LibraryEntry[];
}

export interface DemoScenarioMeta {
  readonly id: DemoScenarioId;
  readonly title: string;
  readonly story: string;
  readonly statChips: readonly string[];
}

export interface DemoScenarioDefinition extends DemoScenarioMeta {
  readonly startWeightKg: number;
  readonly currentWeightKg: number;
  readonly goalWeightKg: number;
  readonly goalKcal: number;
  readonly planDaysAgo: number;
  readonly chatReply: string;
  readonly createUser: (anchorDate: number) => DemoUserProfile;
  readonly createEntries: (anchorDate: number) => { foods: DemoFoodEntry[]; exercises: DemoExerciseEntry[] };
  readonly createWeights: (anchorDate: number) => DemoWeightPoint[];
  readonly createMemories: (anchorDate: number) => DemoMemory[];
  readonly createChat: () => ChatItem[];
  readonly plan: DemoPlanData;
  readonly library: DemoLibraryData;
}

// --- Shared meal pool -------------------------------------------------------
// [name, kcal, proteinG, carbsG, fatG, fiberG]

type MealSeed = readonly [string, number, number, number, number, number];
type ExerciseSeed = readonly [string, number, number]; // [type, durationMin, burnedKcal]

const MEAL = {
  yoghurt: ["Greek yoghurt with berries and honey", 320, 24, 38, 7, 5],
  oats: ["Oats with banana and peanut butter", 410, 15, 58, 14, 8],
  overnightOats: ["Overnight oats with chia and mango", 390, 17, 52, 13, 9],
  paratha: ["Egg paratha with chai", 450, 16, 48, 22, 3],
  biryani: ["Chicken biryani (home-style)", 640, 38, 78, 18, 4],
  karahi: ["Chicken karahi with 1 roti", 560, 42, 34, 24, 5],
  daal: ["Daal chawal with kachumber salad", 520, 22, 82, 12, 9],
  saladBowl: ["Grilled chicken salad bowl", 450, 38, 22, 20, 6],
  sandwich: ["Turkey and cheese sandwich", 380, 26, 40, 12, 4],
  chaat: ["Chana chaat", 290, 13, 44, 7, 9],
  shake: ["Protein shake with milk", 220, 27, 14, 5, 1],
  apple: ["Apple with almonds", 195, 4, 25, 8, 4],
  latte: ["Oat latte", 160, 6, 20, 6, 2],
  feast: ["Family dinner: seekh kebab, naan and kheer", 890, 40, 70, 48, 4],
} as const satisfies Record<string, MealSeed>;

const WALK: ExerciseSeed = ["Brisk walk", 35, 185];
const STRENGTH: ExerciseSeed = ["Strength training", 40, 230];
const YOGA: ExerciseSeed = ["Yoga", 30, 120];

/** Meal loggedAt hours by position within a day (breakfast → dinner → evening). */
const MEAL_HOURS = [8, 13, 16, 19, 21] as const;

interface DaySeed {
  readonly offset: number;
  readonly meals: readonly MealSeed[];
  readonly exercises?: readonly ExerciseSeed[];
}

function demoId<Table extends "foodEntries" | "exerciseEntries" | "foodMemories">(value: string): Id<Table> {
  return value as Id<Table>;
}

function buildEntries(
  scenarioId: DemoScenarioId,
  anchorDate: number,
  days: readonly DaySeed[],
): { foods: DemoFoodEntry[]; exercises: DemoExerciseEntry[] } {
  const foods: DemoFoodEntry[] = [];
  const exercises: DemoExerciseEntry[] = [];
  for (const day of days) {
    const date = anchorDate + day.offset * DAY_MS;
    day.meals.forEach((meal, index) => {
      const hour = MEAL_HOURS[Math.min(index, MEAL_HOURS.length - 1)] ?? 8;
      const loggedAt = date + hour * 3_600_000;
      foods.push({
        _id: demoId(`demo-food-${scenarioId}-${day.offset}-${index}`),
        _creationTime: loggedAt,
        userId: "demo-user" as Id<"users">,
        date,
        name: meal[0],
        estimatedKcal: meal[1],
        finalKcal: meal[1],
        proteinG: meal[2],
        carbsG: meal[3],
        fatG: meal[4],
        fiberG: meal[5],
        source: "manual",
        status: "logged",
        loggedAt,
      });
    });
    (day.exercises ?? []).forEach((exercise, index) => {
      const loggedAt = date + (18 + index) * 3_600_000;
      exercises.push({
        _id: demoId(`demo-exercise-${scenarioId}-${day.offset}-${index}`),
        _creationTime: loggedAt,
        userId: "demo-user" as Id<"users">,
        date,
        type: exercise[0],
        durationMin: exercise[1],
        estimatedBurnedKcal: exercise[2],
        finalBurnedKcal: exercise[2],
        source: "manual",
        status: "logged",
        loggedAt,
      });
    });
  }
  return { foods, exercises };
}

function buildUser(
  anchorDate: number,
  fields: {
    name: string;
    email: string;
    heightCm: number;
    age: number;
    gender: string;
    activityLevel: string;
    startWeightKg: number;
    currentWeightKg: number;
    goalWeightKg: number;
    goalKcal: number;
    proteinG: number;
    carbsG: number;
    fatG: number;
    fiberG: number;
    planDaysAgo: number;
  },
): DemoUserProfile {
  return {
    _id: "demo-user" as Id<"users">,
    _creationTime: anchorDate - fields.planDaysAgo * DAY_MS,
    name: fields.name,
    email: fields.email,
    tokenIdentifier: "demo-token",
    sappsUserId: "demo-sapps-user",
    dailyGoalKcal: fields.goalKcal,
    dailyProteinG: fields.proteinG,
    dailyCarbsG: fields.carbsG,
    dailyFatG: fields.fatG,
    dailyFiberG: fields.fiberG,
    weightKg: fields.currentWeightKg,
    startingWeightKg: fields.startWeightKg,
    targetWeightKg: fields.goalWeightKg,
    heightCm: fields.heightCm,
    age: fields.age,
    gender: fields.gender,
    activityLevel: fields.activityLevel,
    goalType: "lose",
    dietType: "balanced",
    unitWeight: "kg",
    unitDistance: "km",
    planStartAt: anchorDate - fields.planDaysAgo * DAY_MS,
    phaseDurationDays: 60,
    onboardingComplete: true,
  };
}

function chatText(scenarioId: DemoScenarioId, role: "user" | "assistant", text: string, index: number, imageUrl?: string): ChatItem {
  return {
    kind: "text",
    role,
    text,
    ...(imageUrl === undefined ? {} : { imageUrl }),
    messageId: `demo-${scenarioId}-${role}-${index}`,
    messageIndex: index,
  };
}

function memory(
  scenarioId: DemoScenarioId,
  key: string,
  meal: MealSeed,
  servingSize: string,
  confidence: string,
  timesUsed: number,
  createdAt: number,
): DemoMemory {
  return {
    _id: demoId(`demo-memory-${scenarioId}-${key}`),
    _creationTime: createdAt,
    userId: "demo-user" as Id<"users">,
    foodNameLower: meal[0].toLowerCase(),
    foodName: meal[0],
    servingSize,
    calories: meal[1],
    proteinG: meal[2],
    carbsG: meal[3],
    fatG: meal[4],
    fiberG: meal[5],
    confidence,
    source: "manual",
    createdAt,
    lastUsedAt: createdAt,
    timesUsed,
  };
}

function recentEntry(scenarioId: DemoScenarioId, key: string, meal: MealSeed, mealType: string, index: number): LibraryEntry {
  return {
    _id: `demo-recent-${scenarioId}-${key}`,
    name: meal[0],
    quantity: 1,
    unit: "serving",
    finalKcal: meal[1],
    proteinG: meal[2],
    carbsG: meal[3],
    fatG: meal[4],
    fiberG: meal[5],
    mealType,
    dataSource: index % 2 === 0 ? "ai-estimate" : "user-entered",
    confidence: index % 2 === 0 ? "medium" : "high",
    status: "logged",
  };
}

function frequentEntry(scenarioId: DemoScenarioId, key: string, meal: MealSeed, mealType: string, count: number): FrequentFoodView {
  return {
    _id: `demo-freq-${scenarioId}-${key}`,
    name: meal[0],
    quantity: 1,
    unit: "serving",
    finalKcal: meal[1],
    proteinG: meal[2],
    carbsG: meal[3],
    fatG: meal[4],
    fiberG: meal[5],
    mealType,
    count,
    status: "logged",
  };
}

// --- Scenario: Steady progress (Ayesha) --------------------------------------

const PROGRESS_MENU_A: readonly MealSeed[] = [MEAL.oats, MEAL.karahi, MEAL.apple, MEAL.daal, MEAL.latte];
const PROGRESS_MENU_B: readonly MealSeed[] = [MEAL.yoghurt, MEAL.sandwich, MEAL.shake, MEAL.biryani, MEAL.chaat];
const PROGRESS_MENU_C: readonly MealSeed[] = [MEAL.paratha, MEAL.saladBowl, MEAL.shake, MEAL.daal, MEAL.latte];
const PROGRESS_FEAST: readonly MealSeed[] = [MEAL.paratha, MEAL.biryani, MEAL.feast, MEAL.latte];
const PROGRESS_TODAY: readonly MealSeed[] = [MEAL.oats, MEAL.karahi];

function progressDays(): DaySeed[] {
  const rotation = [PROGRESS_MENU_A, PROGRESS_MENU_B, PROGRESS_MENU_C];
  const exercises = [WALK, STRENGTH, YOGA];
  const days: DaySeed[] = [];
  for (let offset = -21; offset <= -1; offset += 1) {
    const slot = ((offset % 3) + 3) % 3;
    if (offset === -9) {
      days.push({ offset, meals: [], exercises: [WALK] });
      continue;
    }
    if (offset === -13) {
      days.push({ offset, meals: PROGRESS_FEAST });
      continue;
    }
    const menu = rotation[slot] ?? PROGRESS_MENU_A;
    const day: DaySeed = { offset, meals: menu };
    if (slot === 1) {
      days.push({ ...day, exercises: [exercises[Math.abs(offset / 3) % exercises.length] ?? WALK] });
      continue;
    }
    days.push(day);
  }
  days.push({ offset: 0, meals: PROGRESS_TODAY, exercises: [WALK] });
  return days;
}

function progressWeights(anchorDate: number): DemoWeightPoint[] {
  const points: DemoWeightPoint[] = [];
  for (let offset = -21; offset <= 0; offset += 1) {
    const base = 86 - (5.6 / 56) * (offset + 56);
    const noise = Math.sin(offset * 2.7) * 0.25;
    points.push({ date: anchorDate + offset * DAY_MS, weightKg: Math.round((base + noise) * 10) / 10 });
  }
  return points;
}

const PROGRESS_PLAN: DemoPlanData = {
  profile: {
    goalType: "lose",
    weightKg: 80.4,
    startingWeightKg: 86,
    targetWeightKg: 78,
    weightLossPace: "steady",
    activityLevel: "moderate",
    avgDailySteps: 8000,
    exerciseType: "mixed",
    planStartedAt: DEMO_PLAN_NOW - 56 * DAY_MS,
    dailyGoalKcal: 1850,
    dailyProteinG: 140,
    dailyCarbsG: 183,
    dailyFatG: 62,
    dailyFiberG: 28,
    unitWeight: "kg",
  },
  snapshot: {
    trendKg: 80.4,
    driftKgPerWeek: -0.65,
    trendConfidence: "high",
    expenditureKcal: 2400,
    expenditureKind: "personalised",
    expenditureConfidence: "high",
    bmrKcal: 1529,
    bmi: 29.5,
    bmiClass: "overweight",
    targetKcal: 1850,
    weeklyChangeKg: -0.5,
    weeksToGoal: 5,
    weeksMin: 4,
    weeksMax: 7,
    goalDateMin: DEMO_PLAN_NOW + 28 * DAY_MS,
    goalDateMax: DEMO_PLAN_NOW + 49 * DAY_MS,
    dataQuality: "high",
    restricted: false,
    weeksOffTrack: 0,
    lastRecalcAt: DEMO_PLAN_NOW,
    engineVersion: "1.0.0",
  },
  pending: null,
  checkins: [
    {
      id: "demo-progress-checkin-3",
      createdAt: DEMO_PLAN_NOW - 7 * DAY_MS,
      previousTargetKcal: 1900,
      newTargetKcal: 1850,
      adjustmentKcal: -50,
      adjusted: true,
      paused: false,
      explanation: "Your expenditure estimate tightened after 7 weeks of complete logs, so the same pace now needs a 50 kcal lower target.",
      reasonsJson: JSON.stringify(["Your weight trend is well established from 22 weigh-ins.", "Your personalised expenditure is based on 8 weeks of complete logs."]),
      status: "accepted",
    },
    {
      id: "demo-progress-checkin-2",
      createdAt: DEMO_PLAN_NOW - 21 * DAY_MS,
      previousTargetKcal: 1950,
      newTargetKcal: 1900,
      adjustmentKcal: -50,
      adjusted: true,
      paused: false,
      explanation: "Your trend ran slightly slower than planned, so the target came down 50 kcal.",
      reasonsJson: JSON.stringify(["Your trend ran slightly slower than planned that week."]),
      status: "accepted",
    },
    {
      id: "demo-progress-checkin-1",
      createdAt: DEMO_PLAN_NOW - 49 * DAY_MS,
      previousTargetKcal: 2000,
      newTargetKcal: 1950,
      adjustmentKcal: -50,
      adjusted: true,
      paused: false,
      explanation: "Your first weekly check-in refined the initial estimate with real logs.",
      reasonsJson: JSON.stringify(["Your first full week of logs refined the initial expenditure estimate."]),
      status: "accepted",
    },
  ],
  calcHistory: [
    {
      kind: "calorieTarget",
      engineVersion: "1.0.0",
      algorithmVersion: "1.0.0",
      confidence: "high",
      explanation: "Your daily target of 1,850 kcal aims for about 0.5 kg per week: it sits roughly 550 kcal below your personalised expenditure of 2,400 kcal.",
      createdAt: DEMO_PLAN_NOW - DAY_MS,
    },
    {
      kind: "adaptiveExpenditure",
      engineVersion: "1.0.0",
      algorithmVersion: "1.0.0",
      confidence: "high",
      explanation: "Expenditure is personalised from 8 weeks of complete logs and 22 weigh-ins.",
      createdAt: DEMO_PLAN_NOW - DAY_MS,
    },
    {
      kind: "macroTargets",
      engineVersion: "1.0.0",
      algorithmVersion: "1.0.0",
      confidence: "high",
      explanation: "Protein is prioritised to protect lean mass; fat has a healthy minimum; fibre scales with calories.",
      createdAt: DEMO_PLAN_NOW - DAY_MS,
    },
  ],
  currentWeightKg: 80.4,
};

// --- Scenario: Fresh start (Daniyal) -----------------------------------------

const FRESH_TODAY: readonly MealSeed[] = [MEAL.yoghurt, MEAL.latte];

function freshDays(): DaySeed[] {
  return [
    { offset: -5, meals: [MEAL.oats, MEAL.biryani, MEAL.shake, MEAL.daal] },
    { offset: -4, meals: [MEAL.paratha, MEAL.sandwich, MEAL.karahi, MEAL.latte] },
    { offset: -2, meals: [MEAL.yoghurt, MEAL.biryani, MEAL.chaat, MEAL.shake], exercises: [["Brisk walk", 25, 130]] },
    { offset: -1, meals: [MEAL.oats, MEAL.sandwich, MEAL.karahi, MEAL.apple] },
    { offset: 0, meals: FRESH_TODAY },
  ];
}

function freshWeights(anchorDate: number): DemoWeightPoint[] {
  return [
    { date: anchorDate - 6 * DAY_MS, weightKg: 96 },
    { date: anchorDate, weightKg: 95.2 },
  ];
}

const FRESH_PLAN: DemoPlanData = {
  profile: {
    goalType: "lose",
    weightKg: 95.2,
    startingWeightKg: 96,
    targetWeightKg: 80,
    weightLossPace: "steady",
    activityLevel: "light",
    avgDailySteps: 5500,
    exerciseType: "walking",
    planStartedAt: DEMO_PLAN_NOW - 6 * DAY_MS,
    dailyGoalKcal: 2100,
    dailyProteinG: 145,
    dailyCarbsG: 236,
    dailyFatG: 64,
    dailyFiberG: 30,
    unitWeight: "kg",
  },
  snapshot: {
    trendKg: 95.2,
    driftKgPerWeek: -0.9,
    trendConfidence: "learning",
    expenditureKcal: 2650,
    expenditureKind: "initial",
    expenditureConfidence: "low",
    bmrKcal: 1931,
    bmi: 28.4,
    bmiClass: "overweight",
    targetKcal: 2100,
    weeklyChangeKg: -0.5,
    weeksToGoal: 22,
    weeksMin: 18,
    weeksMax: 28,
    goalDateMin: DEMO_PLAN_NOW + 126 * DAY_MS,
    goalDateMax: DEMO_PLAN_NOW + 196 * DAY_MS,
    dataQuality: "learning",
    restricted: false,
    weeksOffTrack: 0,
    lastRecalcAt: DEMO_PLAN_NOW,
    engineVersion: "1.0.0",
  },
  pending: null,
  checkins: [],
  calcHistory: [
    {
      kind: "calorieTarget",
      engineVersion: "1.0.0",
      algorithmVersion: "1.0.0",
      confidence: "low",
      explanation: "Your starting target of 2,100 kcal is an initial estimate from your profile — it refines automatically after your first weekly check-ins.",
      createdAt: DEMO_PLAN_NOW - 6 * DAY_MS,
    },
    {
      kind: "adaptiveExpenditure",
      engineVersion: "1.0.0",
      algorithmVersion: "1.0.0",
      confidence: "low",
      explanation: "Expenditure is an initial estimate; it personalises once you have 7 or more days of complete logs.",
      createdAt: DEMO_PLAN_NOW - 6 * DAY_MS,
    },
    {
      kind: "macroTargets",
      engineVersion: "1.0.0",
      algorithmVersion: "1.0.0",
      confidence: "low",
      explanation: "Protein is prioritised to protect lean mass; fat has a healthy minimum; fibre scales with calories.",
      createdAt: DEMO_PLAN_NOW - 6 * DAY_MS,
    },
  ],
  currentWeightKg: 95.2,
};

// --- Scenario: The comeback (Maria) ------------------------------------------

const COMEBACK_MENU_D: readonly MealSeed[] = [MEAL.overnightOats, MEAL.saladBowl, MEAL.shake, MEAL.daal, MEAL.chaat];
const COMEBACK_MENU_E: readonly MealSeed[] = [MEAL.paratha, MEAL.sandwich, MEAL.biryani, MEAL.apple];
const COMEBACK_TODAY: readonly MealSeed[] = [MEAL.oats, MEAL.saladBowl];

function comebackDays(): DaySeed[] {
  const plateau: readonly (readonly MealSeed[])[] = [
    [MEAL.latte],
    [],
    [MEAL.sandwich],
    [MEAL.biryani],
    [MEAL.oats],
    [],
    [MEAL.latte, MEAL.karahi],
    [MEAL.sandwich],
    [MEAL.daal],
    [],
    [MEAL.oats, MEAL.apple],
    [MEAL.latte],
  ];
  const days: DaySeed[] = [];
  for (let offset = -21; offset <= -10; offset += 1) {
    days.push({ offset, meals: plateau[offset + 21] ?? [] });
  }
  for (let offset = -9; offset <= -1; offset += 1) {
    const menu = (offset + 9) % 2 === 0 ? COMEBACK_MENU_D : COMEBACK_MENU_E;
    const day: DaySeed = { offset, meals: menu };
    if (offset === -9) days.push({ ...day, exercises: [WALK] });
    else if (offset === -6) days.push({ ...day, exercises: [STRENGTH] });
    else if (offset === -3) days.push({ ...day, exercises: [WALK] });
    else days.push(day);
  }
  days.push({ offset: 0, meals: COMEBACK_TODAY, exercises: [STRENGTH] });
  return days;
}

function comebackWeights(anchorDate: number): DemoWeightPoint[] {
  const points: DemoWeightPoint[] = [];
  for (let offset = -21; offset <= 0; offset += 1) {
    const value = offset <= -10
      ? 79.5 + Math.sin(offset * 3.1) * 0.2
      : 79.4 - (offset + 10) * 0.08 + Math.sin(offset * 2.3) * 0.15;
    points.push({ date: anchorDate + offset * DAY_MS, weightKg: Math.round(value * 10) / 10 });
  }
  return points;
}

const COMEBACK_PENDING: CheckinView = {
  id: "demo-comeback-checkin",
  createdAt: DEMO_PLAN_NOW - DAY_MS,
  previousTargetKcal: 1900,
  newTargetKcal: 1750,
  adjustmentKcal: -150,
  adjusted: true,
  paused: false,
  explanation: "Ten days of complete logs show your daily expenditure is lower than estimated, so keeping your planned pace means a slightly lower target.",
  reasonsJson: JSON.stringify([
    "Your weight stalled for 2 weeks while logging was incomplete.",
    "Your trend has bent down again over the last 10 days.",
    "Your personalised expenditure is now based on 10 days of complete logs.",
  ]),
  status: "pending",
};

const COMEBACK_PLAN: DemoPlanData = {
  profile: {
    goalType: "lose",
    weightKg: 78.6,
    startingWeightKg: 84,
    targetWeightKg: 72,
    weightLossPace: "steady",
    activityLevel: "moderate",
    avgDailySteps: 7000,
    exerciseType: "mixed",
    planStartedAt: DEMO_PLAN_NOW - 42 * DAY_MS,
    dailyGoalKcal: 1900,
    dailyProteinG: 140,
    dailyCarbsG: 191,
    dailyFatG: 64,
    dailyFiberG: 26,
    unitWeight: "kg",
  },
  snapshot: {
    trendKg: 78.6,
    driftKgPerWeek: -0.55,
    trendConfidence: "moderate",
    expenditureKcal: 2400,
    expenditureKind: "personalised",
    expenditureConfidence: "moderate",
    bmrKcal: 1470,
    bmi: 27.8,
    bmiClass: "overweight",
    targetKcal: 1900,
    weeklyChangeKg: -0.5,
    weeksToGoal: 14,
    weeksMin: 12,
    weeksMax: 17,
    goalDateMin: DEMO_PLAN_NOW + 84 * DAY_MS,
    goalDateMax: DEMO_PLAN_NOW + 119 * DAY_MS,
    dataQuality: "moderate",
    restricted: false,
    weeksOffTrack: 2,
    lastRecalcAt: DEMO_PLAN_NOW,
    engineVersion: "1.0.0",
  },
  pending: COMEBACK_PENDING,
  checkins: [
    COMEBACK_PENDING,
    {
      id: "demo-comeback-checkin-prev",
      createdAt: DEMO_PLAN_NOW - 28 * DAY_MS,
      previousTargetKcal: 1950,
      newTargetKcal: 1900,
      adjustmentKcal: -50,
      adjusted: true,
      paused: false,
      explanation: "Your target was lowered slightly after slower-than-planned progress.",
      reasonsJson: JSON.stringify(["Your trend ran slower than planned that week."]),
      status: "accepted",
    },
  ],
  calcHistory: [
    {
      kind: "calorieTarget",
      engineVersion: "1.0.0",
      algorithmVersion: "1.0.0",
      confidence: "moderate",
      explanation: "Your daily target of 1,900 kcal sits roughly 500 kcal below your estimated expenditure of 2,400 kcal.",
      createdAt: DEMO_PLAN_NOW - DAY_MS,
    },
    {
      kind: "adaptiveExpenditure",
      engineVersion: "1.0.0",
      algorithmVersion: "1.0.0",
      confidence: "moderate",
      explanation: "Expenditure estimate updated from 10 days of complete logs and your weight trend.",
      createdAt: DEMO_PLAN_NOW - DAY_MS,
    },
    {
      kind: "macroTargets",
      engineVersion: "1.0.0",
      algorithmVersion: "1.0.0",
      confidence: "moderate",
      explanation: "Protein is prioritised to protect lean mass; fat has a healthy minimum; fibre scales with calories.",
      createdAt: DEMO_PLAN_NOW - DAY_MS,
    },
  ],
  currentWeightKg: 78.6,
};

// --- Registry ----------------------------------------------------------------

export const DEMO_SCENARIOS: Record<DemoScenarioId, DemoScenarioDefinition> = {
  progress: {
    id: "progress",
    title: "Steady progress",
    story: "Ayesha is 8 weeks in, 5.6 kg down, and right on plan.",
    statChips: ["−5.6 kg", "Week 8", "High data quality"],
    startWeightKg: 86,
    currentWeightKg: 80.4,
    goalWeightKg: 78,
    goalKcal: 1850,
    planDaysAgo: 56,
    chatReply: "This preview gives fixed replies, but in the full app I'd answer with your real logs — you're 5.6 kg down and 70% of the way to 78 kg. Install BiteTrack to keep the streak going.",
    createUser: (anchorDate) => buildUser(anchorDate, {
      name: "Ayesha Khan",
      email: "ayesha.demo@example.com",
      heightCm: 165,
      age: 29,
      gender: "female",
      activityLevel: "moderate",
      startWeightKg: 86,
      currentWeightKg: 80.4,
      goalWeightKg: 78,
      goalKcal: 1850,
      proteinG: 140,
      carbsG: 183,
      fatG: 62,
      fiberG: 28,
      planDaysAgo: 56,
    }),
    createEntries: (anchorDate) => buildEntries("progress", anchorDate, progressDays()),
    createWeights: progressWeights,
    createMemories: (anchorDate) => [
      memory("progress", "biryani", MEAL.biryani, "1 plate", "high", 9, anchorDate - 2 * DAY_MS),
      memory("progress", "oats", MEAL.oats, "1 bowl", "high", 7, anchorDate - DAY_MS),
      memory("progress", "latte", MEAL.latte, "12 oz", "medium", 5, anchorDate - 3 * DAY_MS),
    ],
    createChat: () => [
      chatText("progress", "assistant", "Salaam Ayesha — you crossed the 5 kg mark this morning: 86.0 → 80.4 kg in 8 weeks, right on plan.", 0),
      chatText("progress", "user", "I can finally feel the difference. Was my biryani on Sunday okay?", 1),
      chatText("progress", "assistant", "Your home-style chicken biryani came to about **640 kcal** — it fit your 1,850 kcal day because breakfast stayed light. Your weekly average is still 210 kcal under target.", 2),
      chatText("progress", "user", "Can I keep losing 0.7 kg a week?", 3),
      chatText("progress", "assistant", "You're trending at 0.65 kg/week with high-confidence data. Expect the pace to ease toward 0.5 kg/week as you near 78 kg — that's normal and keeps the loss sustainable.", 4),
      chatText("progress", "assistant", "Install BiteTrack to keep your own streak going with photo estimates and weekly check-ins.", 5),
    ],
    plan: PROGRESS_PLAN,
    library: {
      recent: [
        recentEntry("progress", "biryani", MEAL.biryani, "lunch", 0),
        recentEntry("progress", "karahi", MEAL.karahi, "dinner", 1),
        {
          _id: "demo-recent-progress-haleem",
          name: "Homemade haleem (large bowl)",
          mealType: "dinner",
          kcalUnknown: true,
          dataSource: "ai-estimate",
          confidence: "low",
          status: "logged",
        },
      ],
      frequent: [
        frequentEntry("progress", "karahi", MEAL.karahi, "dinner", 7),
        frequentEntry("progress", "daal", MEAL.daal, "dinner", 5),
        frequentEntry("progress", "latte", MEAL.latte, "breakfast", 5),
      ],
      yesterday: [
        recentEntry("progress", "y-paratha", MEAL.paratha, "breakfast", 1),
        recentEntry("progress", "y-salad", MEAL.saladBowl, "lunch", 0),
        recentEntry("progress", "y-shake", MEAL.shake, "snack", 1),
        recentEntry("progress", "y-daal", MEAL.daal, "dinner", 0),
      ],
    },
  },
  fresh: {
    id: "fresh",
    title: "Fresh start",
    story: "Daniyal just finished onboarding and logged his first photo meal.",
    statChips: ["Week 1", "96.0 → 95.2 kg", "Learning mode"],
    startWeightKg: 96,
    currentWeightKg: 95.2,
    goalWeightKg: 80,
    goalKcal: 2100,
    planDaysAgo: 6,
    chatReply: "This preview gives fixed replies, but in the full app I'd coach you through week 1 — photo logs, portions, and your first check-in. Install BiteTrack to start for real.",
    createUser: (anchorDate) => buildUser(anchorDate, {
      name: "Daniyal Raza",
      email: "daniyal.demo@example.com",
      heightCm: 183,
      age: 34,
      gender: "male",
      activityLevel: "light",
      startWeightKg: 96,
      currentWeightKg: 95.2,
      goalWeightKg: 80,
      goalKcal: 2100,
      proteinG: 145,
      carbsG: 236,
      fatG: 64,
      fiberG: 30,
      planDaysAgo: 6,
    }),
    createEntries: (anchorDate) => buildEntries("fresh", anchorDate, freshDays()),
    createWeights: freshWeights,
    createMemories: (anchorDate) => [
      memory("fresh", "yoghurt", MEAL.yoghurt, "1 cup", "high", 2, anchorDate - 2 * DAY_MS),
      memory("fresh", "latte", MEAL.latte, "12 oz", "medium", 2, anchorDate - DAY_MS),
    ],
    createChat: () => [
      chatText("fresh", "user", "First try — is this a good breakfast?", 0, "/demo-food.jpg"),
      chatText("fresh", "assistant", "Nice first photo log! That looks like Greek yoghurt with berries and honey — about **320 kcal** with 24 g protein. High-protein breakfasts like this make your 2,100 kcal target much easier to hit.", 1),
      chatText("fresh", "user", "How does this work exactly?", 2),
      chatText("fresh", "assistant", "Snap a photo or describe your meal — I estimate the calories and macros, you confirm, and it's logged. After about a week of logs and two weigh-ins, I'll personalise your daily target to your real expenditure.", 3),
      chatText("fresh", "user", "And my weight is already down?", 4),
      chatText("fresh", "assistant", "96.0 → 95.2 kg since your first weigh-in. Early drops are partly water, so expect the trend to settle — your first weekly check-in arrives in a few days.", 5),
    ],
    plan: FRESH_PLAN,
    library: {
      recent: [
        recentEntry("fresh", "yoghurt", MEAL.yoghurt, "breakfast", 0),
        recentEntry("fresh", "latte", MEAL.latte, "breakfast", 1),
        recentEntry("fresh", "oats", MEAL.oats, "breakfast", 0),
      ],
      frequent: [
        frequentEntry("fresh", "yoghurt", MEAL.yoghurt, "breakfast", 2),
        frequentEntry("fresh", "oats", MEAL.oats, "breakfast", 2),
      ],
      yesterday: [
        recentEntry("fresh", "y-oats", MEAL.oats, "breakfast", 1),
        recentEntry("fresh", "y-sandwich", MEAL.sandwich, "lunch", 0),
        recentEntry("fresh", "y-karahi", MEAL.karahi, "dinner", 1),
        recentEntry("fresh", "y-apple", MEAL.apple, "snack", 0),
      ],
    },
  },
  comeback: {
    id: "comeback",
    title: "The comeback",
    story: "Maria broke a 2-week plateau and has a plan update waiting.",
    statChips: ["Week 6", "Plateau → −0.8 kg", "Plan update ready"],
    startWeightKg: 84,
    currentWeightKg: 78.6,
    goalWeightKg: 72,
    goalKcal: 1900,
    planDaysAgo: 42,
    chatReply: "Fixed replies in this preview — in the full app I'd dig into your real logs. Your streak already broke the plateau: 78.6 kg and bending down. Install BiteTrack to accept your plan update.",
    createUser: (anchorDate) => buildUser(anchorDate, {
      name: "Maria Santos",
      email: "maria.demo@example.com",
      heightCm: 168,
      age: 41,
      gender: "female",
      activityLevel: "moderate",
      startWeightKg: 84,
      currentWeightKg: 78.6,
      goalWeightKg: 72,
      goalKcal: 1900,
      proteinG: 140,
      carbsG: 191,
      fatG: 64,
      fiberG: 26,
      planDaysAgo: 42,
    }),
    createEntries: (anchorDate) => buildEntries("comeback", anchorDate, comebackDays()),
    createWeights: comebackWeights,
    createMemories: (anchorDate) => [
      memory("comeback", "biryani", ["Chicken biryani (restaurant)", 780, 36, 82, 34, 4], "1 plate", "medium", 6, anchorDate - 4 * DAY_MS),
      memory("comeback", "chaat", MEAL.chaat, "1 bowl", "high", 5, anchorDate - 2 * DAY_MS),
      memory("comeback", "shake", MEAL.shake, "1 scoop + milk", "high", 5, anchorDate - DAY_MS),
    ],
    createChat: () => [
      chatText("comeback", "user", "Why did my weight stall for two weeks?", 0),
      chatText("comeback", "assistant", "Two things lined up: logging slipped to about one meal a day, and restaurant biryani hides a lot of oil — a '640 kcal' plate can quietly be 900+. Your trend started bending down again after 10 days of complete logs.", 1),
      chatText("comeback", "user", "So the biryani was the problem?", 2),
      chatText("comeback", "assistant", "Partly. When you log biryani, add a note like 'restaurant, oily' and I'll estimate the ghee separately. I've also drafted your weekly check-in — the recent streak sharpened your expenditure estimate.", 3),
      chatText("comeback", "assistant", "Keep logging completely for 4+ days this week to hold your data quality up — that's exactly what unlocked the plan update waiting on your Plan tab.", 4),
    ],
    plan: COMEBACK_PLAN,
    library: {
      recent: [
        recentEntry("comeback", "salad", MEAL.saladBowl, "lunch", 0),
        recentEntry("comeback", "shake", MEAL.shake, "snack", 1),
        recentEntry("comeback", "chaat", MEAL.chaat, "snack", 0),
      ],
      frequent: [
        frequentEntry("comeback", "salad", MEAL.saladBowl, "lunch", 8),
        frequentEntry("comeback", "oats", MEAL.oats, "breakfast", 6),
        frequentEntry("comeback", "shake", MEAL.shake, "snack", 5),
      ],
      yesterday: [
        recentEntry("comeback", "y-paratha", MEAL.paratha, "breakfast", 1),
        recentEntry("comeback", "y-sandwich", MEAL.sandwich, "lunch", 0),
        recentEntry("comeback", "y-biryani", MEAL.biryani, "dinner", 1),
        recentEntry("comeback", "y-apple", MEAL.apple, "snack", 0),
      ],
    },
  },
};

export const DEMO_SCENARIO_LIST: readonly DemoScenarioDefinition[] = DEMO_SCENARIO_IDS.map((id) => DEMO_SCENARIOS[id]);

/** Reads the persisted scenario choice; falls back to the default when absent/invalid. */
export function readStoredDemoScenario(): DemoScenarioId {
  if (typeof window === "undefined") return DEFAULT_DEMO_SCENARIO;
  try {
    const raw = window.localStorage.getItem(DEMO_SCENARIO_STORAGE_KEY);
    if (raw === "progress" || raw === "fresh" || raw === "comeback") return raw;
  } catch {
    console.warn("[Frontend][Demo] Could not read stored demo scenario");
  }
  return DEFAULT_DEMO_SCENARIO;
}

export function persistDemoScenario(id: DemoScenarioId): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DEMO_SCENARIO_STORAGE_KEY, id);
  } catch {
    console.warn("[Frontend][Demo] Could not persist demo scenario", { id });
  }
}
